import cx from 'classnames';
import { useLayoutEffect, useState } from 'react';

import type { IconKind } from '@sb/design-system';

import { OptionsList, type OptionsListOption } from './OptionsList';

/**
 * Tagged result emitted when the user picks a landing option. The
 * downstream send-path turns this into the chat-input label
 * injection + auto-send. Keeping it tagged means consumers don't
 * have to parse a free-form English string to recover intent.
 */
export type LandingOption =
  | { kind: 'build' }
  | { kind: 'buildDashboard' }
  | { kind: 'ask' };

export const BUILD_LABEL = 'Build a routine';
export const BUILD_DASHBOARD_LABEL = 'Build an operator dashboard';
export const ASK_LABEL = 'Ask a question';

export function landingOptionLabel(option: LandingOption): string {
  switch (option.kind) {
    case 'build':
      return BUILD_LABEL;
    case 'buildDashboard':
      return BUILD_DASHBOARD_LABEL;
    case 'ask':
      return ASK_LABEL;
    default: {
      const exhaustiveCheck: never = option;

      return exhaustiveCheck;
    }
  }
}

interface LandingHeroProps {
  /**
   * Fires when the user picks a landing option. The shape carries the
   * intent kind so callers don't have to parse a localised display
   * string.
   */
  onSelectOption: (option: LandingOption) => void;
}

interface LandingOptionDef {
  option: LandingOption;
  title: string;
  iconKind: IconKind;
}

const LANDING_OPTIONS: LandingOptionDef[] = [
  { option: { kind: 'build' }, title: BUILD_LABEL, iconKind: 'pencilSquare' },
  {
    option: { kind: 'buildDashboard' },
    title: BUILD_DASHBOARD_LABEL,
    iconKind: 'squareGrid2x2',
  },
  {
    option: { kind: 'ask' },
    title: ASK_LABEL,
    iconKind: 'questionmarkCircle',
  },
];

const FADE_IN_STYLE_ID = 'routine-agent-chat-landing-keyframes';

function injectFadeInKeyframes() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(FADE_IN_STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = FADE_IN_STYLE_ID;

  style.textContent = `
    @keyframes _routineAgentChatLandingIn {
      from { opacity: 0; transform: translateY(8px); }
      to   { opacity: 1; transform: translateY(0); }
    }
  `;

  document.head.appendChild(style);
}

export function LandingHero({ onSelectOption }: LandingHeroProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Inject before paint so the entrance animation plays on the first
  // open (a regular useEffect would reference a missing keyframe rule).
  useLayoutEffect(() => {
    injectFadeInKeyframes();
  }, []);

  const optionsListItems: OptionsListOption[] = LANDING_OPTIONS.map((o) => ({
    id: o.option.kind,
    iconKind: o.iconKind,
    title: o.title,
  }));

  const handleSelect = (id: string) => {
    setSelectedId(id);
    const picked = LANDING_OPTIONS.find((o) => o.option.kind === id);

    if (picked) onSelectOption(picked.option);
  };

  return (
    <div
      className={cx(
        'tw-flex',
        'tw-flex-1',
        'tw-flex-col',
        'tw-min-h-0',
        'tw-overflow-y-auto',
        'tw-pt-32',
        'tw-px-16',
        'tw-pb-8',
        'tw-gap-24',
      )}
      style={{
        animation:
          '_routineAgentChatLandingIn 0.32s cubic-bezier(0.16, 1, 0.3, 1) forwards',
      }}
    >
      <h3
        className={cx('tw-text-22', 'tw-text-center', 'tw-m-0')}
        style={{
          // 600 weight + `-0.3` letter-spacing aren't in the
          // typography scale (weights jump 500 → 700).
          fontWeight: 600,
          color: 'var(--label-primary)',
          letterSpacing: -0.3,
        }}
      >
        What should we do?
      </h3>
      <OptionsList
        options={optionsListItems}
        selectedId={selectedId}
        onSelect={handleSelect}
      />
    </div>
  );
}
